import { magicNumbers } from './magicNumbers';
import { isEmpty } from '../lib';

/**
 * True only for the resources directory itself (/resources, /resources/:platform)
 * — a free-text search across resource accounts, unrelated to subjects. The
 * single-resource profile (/resources/:platform/:id) and the VIP edit form
 * (/resources/vip/:id/edit) must not match; both callers rely on that split to
 * decide whether "search" here means resourceQuery or the normal subject query.
 */
export const isResourcesListPath = (pathname) =>
  /^\/resources(\/[^/]+)?\/?$/.test(pathname || '');

/**
 * True only for a single resource's profile (/resources/:platform/:id), which
 * filters that resource's own content by subject exactly like the contents and
 * statistics pages, so it must not be treated as the resources list above.
 */
export const isResourceProfilePath = (pathname) =>
  /^\/resources\/[^/]+\/[^/]+\/?$/.test(pathname || '');

export const getCurrentQuery = (queries, queryId) => {
  const _queries = Array.isArray(queries) ? queries : [];
  if (!queryId) {
    throw new Error('queryId is required');
  }
  return _queries.find((query) => query.id === queryId);
};

/**
 * Subject id safe to send as the `query` param. YOUR_SEARCH_QUERY_ID is a
 * client-side sentinel, never a real subject, so an untouched free search
 * resolves to the subject that was selected before it. Read this during render
 * rather than waiting for the store to be corrected by an effect, otherwise the
 * first request of a freshly mounted page still carries the sentinel.
 */
export const getEffectiveQueryId = (currentQuery) => {
  const { queryId, q, freeSearchFallbackQueryId } = currentQuery || {};

  if (queryId === magicNumbers.YOUR_SEARCH_QUERY_ID) {
    // A typed free search travels in q, so it has no subject id at all
    return q ? undefined : freeSearchFallbackQueryId || undefined;
  }
  if (queryId === magicNumbers.MEGA_SUBJECT_QUERY_ID) {
    return undefined;
  }
  return queryId || undefined;
};

/**
 * Same resolution as getEffectiveQueryId, for callers whose params are also
 * piped through ignoreRequestParams. That utility keeps q only when it sees
 * the free-search sentinel in query, but getEffectiveQueryId always resolves
 * a typed free search's subject id away to undefined — content builds its
 * q/query pair separately and never reaches ignoreRequestParams, so it's
 * unaffected, but a statistics or resource-profile hook combining the two
 * would silently lose the typed search text. Preserving the sentinel here
 * only for that one case keeps ignoreRequestParams' own contract untouched.
 */
export const getIgnoreParamsQueryId = (currentQuery) => {
  const { queryId, q } = currentQuery || {};
  if (queryId === magicNumbers.YOUR_SEARCH_QUERY_ID && q) {
    return queryId;
  }
  return getEffectiveQueryId(currentQuery);
};

/**
 * Builds the q/query pair for content requests. The subject id comes from
 * getEffectiveQueryId, the same resolver the statistics charts use, so both
 * pages treat an untouched free search identically and neither can send a
 * sentinel id. Only the q side differs, since content combines the typed free
 * search with the in-page result search.
 */
export const getContentRequestQAndQueryId = ({
  currentQuery,
  resultSearchQuery,
  isActivePlatform = true,
}) => {
  const searchQuery =
    resultSearchQuery && isActivePlatform ? resultSearchQuery : undefined;
  const queryId = getEffectiveQueryId(currentQuery);

  const isMegaSubject =
    currentQuery.queryId === magicNumbers.MEGA_SUBJECT_QUERY_ID;
  const hasSubject =
    Boolean(currentQuery.queryId) &&
    currentQuery.queryId !== magicNumbers.YOUR_SEARCH_QUERY_ID &&
    !isMegaSubject;

  /**
   * A typed free search is the only case carrying a q of its own; a subject, an
   * empty free search and the mega subject all leave q to the result search.
   */
  const isTypedFreeSearch =
    !hasSubject && !isMegaSubject && Boolean(currentQuery.q);

  if (!isTypedFreeSearch) {
    return { q: searchQuery, queryId };
  }

  return {
    q: searchQuery ? `${currentQuery.q}*${searchQuery}` : currentQuery.q,
    queryId,
  };
};

export const createHighlightText = ({
  currentQuery,
  queries,
  resultSearchQuery,
}) => {
  let highlight;

  const parsedQuery = queries?.data?.find(
    (query) => query.id === currentQuery.queryId
  )?.parsed_query;

  if (currentQuery.queryId === magicNumbers.MEGA_SUBJECT_QUERY_ID) {
    highlight = '';
    if (resultSearchQuery) {
      highlight = resultSearchQuery;
    }
  } else if (
    currentQuery.queryId &&
    currentQuery.queryId !== magicNumbers.YOUR_SEARCH_QUERY_ID
  ) {
    if (resultSearchQuery) {
      highlight = `${parsedQuery}*${resultSearchQuery}`;
    } else {
      highlight = parsedQuery;
    }
  } else if (currentQuery.q) {
    if (resultSearchQuery) {
      highlight = `${currentQuery.q}*${resultSearchQuery}`;
    } else {
      highlight = currentQuery.q;
    }
  }
  return highlight;
};

/**
 * Highlight text of one stream: the parsed query of the subject saved with it.
 * A stream without a subject, or whose subject is not in the user's list, gets
 * an empty string, so nothing is highlighted instead of falling back to the
 * subject selected on another page.
 */
export const createStreamHighlightText = ({ params, queries }) => {
  if (!params?.query) return '';

  return (
    queries?.data?.find((query) => String(query.id) === String(params.query))
      ?.parsed_query || ''
  );
};

export const getUserOwnedQueries = (queries) => {
  return queries.filter(
    (query) =>
      isEmpty(query.shared_source) &&
      query.id !== magicNumbers.MEGA_SUBJECT_QUERY_ID &&
      query.id !== magicNumbers.YOUR_SEARCH_QUERY_ID
  );
};

/**
 * check if the current subject is the first query of the user, if the current subject id is not provided, check if the user has no queries
 * @param {Array} queries - the queries array
 * @param {number} currentSubjectId - the current subject id, if not provided, check if the user has no queries
 * @returns {boolean} - true if the current subject is the first query of the user, false otherwise
 */
export const isFirstQueryOfUser = (queries, currentSubjectId) => {
  const userOwnedQueries = getUserOwnedQueries(queries);
  return (
    (!currentSubjectId && userOwnedQueries?.length === 0) ||
    (userOwnedQueries?.length === 1 &&
      userOwnedQueries?.[0]?.id === currentSubjectId)
  );
};
